import { useState } from 'react';
import { Button, Dialog, Input } from '@/core/ui';
import { LockSimple, PencilSimple, Trash } from '@/core/ui/icons';
import {
  deleteVariable,
  renameVariable,
  SYSTEM_VARIABLES,
  toVariableName,
  variableNameProblem,
  variableUsages,
} from '../../domain/variables';
import { useBuilder } from './context';
import { NewVariable } from './fields';

/** As variáveis do fluxo: criar, renomear (as referências acompanham) e apagar. */
export function VariablesPanel() {
  const { doc, update } = useBuilder();
  const [editing, setEditing] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const usages = removing ? variableUsages(doc, removing).length : 0;

  return (
    <div className="space-y-4">
      <NewVariable label="Variável" onCreate={() => undefined} />

      <section>
        <h3 className="mb-2 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">Do fluxo</h3>
        {doc.variables.length === 0 ? (
          <p className="rounded-2xl bg-surface-2 p-3 text-sm text-fg-subtle">
            Nenhuma ainda. As perguntas guardam a resposta numa variável; use-a depois nos textos com{' '}
            <code className="font-mono">{'{{nome}}'}</code>.
          </p>
        ) : (
          <ul className="space-y-1">
            {doc.variables.map((v) =>
              editing === v.name ? (
                <li key={v.name}>
                  <Rename
                    name={v.name}
                    onCancel={() => setEditing(null)}
                    onSave={(next) => {
                      update((d) => renameVariable(d, v.name, next));
                      setEditing(null);
                    }}
                  />
                </li>
              ) : (
                <li key={v.name} className="flex items-center gap-2 rounded-xl bg-surface-2 px-3 py-2">
                  <code className="min-w-0 flex-1 truncate font-mono text-sm text-fg">{v.name}</code>
                  <span className="text-xs text-fg-subtle">{variableUsages(doc, v.name).length} usos</span>
                  <button
                    type="button"
                    aria-label={`Renomear ${v.name}`}
                    title="Renomear"
                    onClick={() => setEditing(v.name)}
                    className="rounded-md p-1 text-fg-subtle hover:bg-surface hover:text-fg"
                  >
                    <PencilSimple aria-hidden className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Apagar ${v.name}`}
                    title="Apagar"
                    onClick={() => setRemoving(v.name)}
                    className="rounded-md p-1 text-fg-subtle hover:bg-surface hover:text-fg"
                  >
                    <Trash aria-hidden className="size-3.5" />
                  </button>
                </li>
              ),
            )}
          </ul>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
          Da pessoa (automáticas)
        </h3>
        <ul className="space-y-1">
          {SYSTEM_VARIABLES.map((v) => (
            <li key={v.name} className="flex items-center gap-2 rounded-xl bg-surface-2 px-3 py-2">
              <LockSimple aria-hidden className="size-3.5 shrink-0 text-fg-subtle" />
              <code className="font-mono text-sm text-fg">{v.name}</code>
              <span className="truncate text-xs text-fg-subtle">{v.label}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-fg-subtle">Só leitura: vêm de quem está conversando.</p>
      </section>

      <Dialog open={removing !== null} onClose={() => setRemoving(null)} title={`Apagar ${removing ?? ''}?`}>
        <p className="text-sm text-fg-muted">
          {usages === 0
            ? 'Nenhum bloco usa esta variável.'
            : `${usages} ${usages === 1 ? 'bloco usa' : 'blocos usam'} esta variável. Os campos que a escolhiam ficam vazios; os {{textos}} que a citam aparecem como problema para você corrigir.`}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setRemoving(null)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              const name = removing;
              if (name) update((d) => deleteVariable(d, name));
              setRemoving(null);
            }}
          >
            Apagar
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function Rename({
  name,
  onSave,
  onCancel,
}: {
  name: string;
  onSave: (n: string) => void;
  onCancel: () => void;
}) {
  const { doc } = useBuilder();
  const [value, setValue] = useState(name);
  const clean = toVariableName(value);
  const problem = variableNameProblem(clean, doc, name);
  const save = () => {
    if (clean === name) onCancel();
    else if (!problem) onSave(clean);
  };
  return (
    <div className="space-y-1 rounded-xl bg-surface-2 p-2">
      <div className="flex gap-2">
        <Input
          autoFocus
          aria-label={`Novo nome de ${name}`}
          value={value}
          maxLength={40}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              save();
            } else if (e.key === 'Escape') onCancel();
          }}
          className="h-9 font-mono"
        />
        <Button size="sm" className="h-9" onClick={save} disabled={!!problem && clean !== name}>
          Salvar
        </Button>
      </div>
      <p className="text-xs text-fg-subtle">
        {problem && clean !== name
          ? problem
          : 'Os blocos e os {{textos}} que a usam passam a usar o nome novo.'}
      </p>
    </div>
  );
}
